import { Inject, Injectable } from "@angular/core";
import { filter, map, Observable, Subject } from "rxjs";

import { BaseSignalRClass } from "@app/core/signalR/base-signalr.class";
import { EBrowserNotifyType, TDocumentStatusChanged } from "@app/core/signalR/browser-notify.type";
import { ISignalRConfig } from "@app/core/signalR/signalr-config.interface";
import { NOTIFY_HUB_CONFIG } from "@app/core/signalR/signalr-injections";
import { EEntityTypeName } from "@app/shared/enums/entity-type.enum";
import { EDocumentGenerationStatus } from "@app/shared/types/policy-document.type";

export interface TNotification {
  id: string;
  type: EBrowserNotifyType;
  title: string;
  attribute: Record<string, unknown>;
  payload?: unknown;
}

const DOCUMENT_GENERATION_STATUSES: readonly string[] = Object.values(EDocumentGenerationStatus);
const ENTITY_TYPE_NAMES: readonly string[] = Object.values(EEntityTypeName);

@Injectable({ providedIn: "root" })
export class NotificationHubService extends BaseSignalRClass {
  private _newNotification$ = new Subject<TNotification>();

  readonly newNotification$ = this._newNotification$.asObservable();

  readonly bellNotification$: Observable<TNotification> = this.newNotification$.pipe(
    filter(notification => notification.type !== EBrowserNotifyType.DocumentStatusChanged),
  );

  readonly documentStatusChanged$: Observable<TDocumentStatusChanged> = this.newNotification$.pipe(
    filter(notification => notification.type === EBrowserNotifyType.DocumentStatusChanged),
    map(notification => this.toDocumentStatusChanged(notification.payload)),
    filter((change): change is TDocumentStatusChanged => change !== null),
  );

  constructor(@Inject(NOTIFY_HUB_CONFIG) config: ISignalRConfig) {
    super(config.hubUrl, config.logLevel, config.accessTokenFactory);
  }

  override connection() {
    super.connection();

    this.registerEvents();
  }

  private registerEvents(): void {
    if (!this.hubConnection) {
      console.warn("Hub connection is not initialized. Call connection() first.");
      return;
    }

    this.hubConnection.on("Notify", (data: TNotification) => {
      this._newNotification$.next(data);
    });
  }

  private toDocumentStatusChanged(payload: unknown): TDocumentStatusChanged | null {
    if (typeof payload !== "object" || payload === null) {
      return null;
    }

    const { documentFileId, status, fileUri, entityType, entityId, policyId, clientId } =
      payload as Record<string, unknown>;

    if (typeof documentFileId !== "string" || typeof status !== "string") {
      return null;
    }

    if (!DOCUMENT_GENERATION_STATUSES.includes(status)) {
      return null;
    }

    return {
      documentFileId,
      status: status as EDocumentGenerationStatus,
      fileUri: this.toNullableString(fileUri),
      entityType: this.toEntityTypeName(entityType),
      entityId: this.toNullableString(entityId),
      policyId: this.toNullableString(policyId),
      clientId: this.toNullableString(clientId),
    };
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === "string" ? value : null;
  }

  private toEntityTypeName(value: unknown): EEntityTypeName | null {
    return typeof value === "string" && ENTITY_TYPE_NAMES.includes(value) ? (value as EEntityTypeName) : null;
  }
}
